// @givmo/board — "Approve by written consent": a consent that names these minutes (CBG-RC9, D-CBG-22).
//
// ══ THE BAND-2 RAILS, AS THIS DIALOG HOLDS THEM ═════════════════════════════════════════════════
//
// The officer chose the act: they pressed it on these minutes. The text is the backend's neutral,
// provenance-labelled form (`GET …/records/{id}/approval-consent-form`), rendered from the minutes'
// own facts — their title, date, record and fingerprint, the `{braced}` terms — with every other term
// a `[bracketed]` field the officer types, exactly as the library's forms (`template-fill.ts`): this
// app fills in nothing of its own, the organization's name included. `TemplateNotice` shows the
// form's review line, provenance and citations verbatim.
//
// The ONE enforced line is the paragraph naming the minutes by their fingerprint (contract §2, Q7):
// circulation refuses a text without it, and only the fingerprint is checked. So the preview marks
// that paragraph, quietly, and the words above the preview say why it stays. The rest of the wording
// stays the board's to edit like any draft, here through the fields and later on the draft itself.
//
// Saving makes a DRAFT through its own operation (`POST …/records/{id}/approval-consents`, the
// circulate permission): it names these minutes from birth, and pins them when it goes out.
//
// CBG-RC9-MULTI (frame `m2`): when the board's two rules send a meeting's minutes and its closed
// sessions' the same way, the one consent names every such set, in the meeting's order — the server
// decides which, at the form and again at the create (contract §M2, C2), and the form serves them as
// `minutes_sets`. Until the form arrives, the rows come from the page's own read (the minutes read's
// `consent_names_with`), so the dialog doesn't grow a row as it loads. The text's fingerprint
// paragraph names every set's fingerprint, so marking it marks them all.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isGivmoError, type BoardMinutesApprovalConsentFormOut } from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { todayAsDateInput } from '../../lib/display';
import { TemplateNotice } from '../consents/template-notice';
import {
  fillTemplateBody,
  isLongFormField,
  remainingFields,
  templateFieldLabel,
  type TemplateFieldValues,
} from '../consents/template-fill';
import { fingerprintParagraph, type NamedSet } from '../consents/minutes-consent-decode';
import { minutesConsentWords as rc9, theMinutesOf } from '../consents/minutes-consent-words';
import {
  closedSessionsAmong,
  minutesSetsWords as several,
  setsAsRows,
  type SetNaming,
} from '../consents/minutes-sets-words';
import { minutesRuleWords as rc11, theClosedMinutesOf } from './minutes-rule-words';

export interface MinutesConsentDialogProps {
  orgRef: string;
  /** The minutes' record. */
  recordId: string;
  /** Their meeting's title and date, which name them in the "Approves" row. */
  meetingTitle: string | null | undefined;
  meetingDate: string | null;
  /**
   * CBG-RC11 (§A7): a closed session's own minutes — named by the session's general purpose, the
   * one public fact about it, never its title. Null for a meeting's own minutes.
   */
  closedSession?: { segmentId?: string; purposeLabel: string | null } | null;
  /**
   * CBG-RC9-MULTI: the other sets of the meeting the consent would name with these, as the page's
   * minutes read says (`approval_way.consent_names_with`), shown until the form serves its own list.
   */
  alsoNames?: readonly NamedSet[];
  /**
   * The meeting's closed sessions in its own order (their segment ids), so the sets named before the
   * form arrives sit where the meeting holds them, this page's own session included (review-149's F6).
   */
  sessionOrder?: readonly string[];
  /**
   * CBG-FIX104: the minutes' committee, or null for the board's. A committee's consent, for its own
   * minutes, is read by its members only, so the closed-session note says whose readers it has.
   */
  committee?: string | null;
  onClose: () => void;
  onCreated: (consentId: string) => void;
}

/** The sets as the form names them, in its order; null until it arrives or when it lists none. */
function formSets(form: BoardMinutesApprovalConsentFormOut | null): SetNaming[] | null {
  const listed = form?.minutes_sets ?? [];
  if (listed.length === 0) return null;
  return listed.map((set) => ({
    closedSession:
      set.closed_session === null || set.closed_session === undefined
        ? null
        : { purposeLabel: set.closed_session.purpose_label ?? null },
  }));
}

/**
 * The sets as the page's read names them, before the form arrives: the meeting's own minutes first,
 * then each closed session's — this page's own and the others the read names — in the meeting's
 * order, as the form serves them (a session the order doesn't name keeps its place after the rest).
 */
function pageSets(
  closedSession: { segmentId?: string; purposeLabel: string | null } | null,
  alsoNames: readonly NamedSet[],
  sessionOrder: readonly string[],
): SetNaming[] {
  const all: { segmentId: string | null; set: SetNaming }[] = [
    {
      segmentId: closedSession?.segmentId ?? null,
      set: {
        closedSession: closedSession === null ? null : { purposeLabel: closedSession.purposeLabel },
      },
    },
    ...alsoNames.map((set) => ({ segmentId: set.closedSession?.segmentId ?? null, set })),
  ];
  const place = (segmentId: string | null) => {
    const at = segmentId === null ? -1 : sessionOrder.indexOf(segmentId);
    return at === -1 ? sessionOrder.length : at;
  };
  const own = all.filter(({ set }) => set.closedSession === null).slice(0, 1);
  const closed = all
    .filter(({ set }) => set.closedSession !== null)
    .sort((left, right) => place(left.segmentId) - place(right.segmentId));
  return [...own, ...closed].map(({ set }) => set);
}

export function MinutesConsentDialog({
  orgRef,
  recordId,
  meetingTitle,
  meetingDate,
  closedSession = null,
  alsoNames = [],
  sessionOrder = [],
  committee = null,
  onClose,
  onCreated,
}: MinutesConsentDialogProps) {
  const committeeMinutes = committee !== null;
  const operations = useBoardOperations();
  // `null` until the officer types: the form's own suggested title stands until then.
  const [title, setTitle] = useState<string | null>(null);
  const [consentDate, setConsentDate] = useState(() => todayAsDateInput());
  const [values, setValues] = useState<TemplateFieldValues>({});
  const [error, setError] = useState<string | null>(null);

  const formQuery = useQuery({
    queryKey: ['board-minutes-approval-consent-form', orgRef, recordId],
    // The minutes as they stand NOW: the form names their current fingerprint, which circulation
    // checks, so a form read before a refresh would only be refused later.
    staleTime: 0,
    retry: false,
    queryFn: async (): Promise<BoardMinutesApprovalConsentFormOut> => {
      const { data } = await operations.officerGetBoardMinutesApprovalConsentForm({
        path: { org_ref: orgRef, record_id: recordId },
        throwOnError: true,
      });
      return data;
    },
  });
  const form = formQuery.data ?? null;
  const fields = useMemo(() => form?.fill_in_fields ?? [], [form]);
  const body = useMemo(
    () => (form === null ? '' : fillTemplateBody(form.body, fields, values)),
    [form, fields, values],
  );
  const stillToFill = useMemo(
    () => (form === null ? [] : remainingFields(body, fields)),
    [form, body, fields],
  );
  const marked = fingerprintParagraph(body, form?.minutes.content_hash);
  const shownTitle = title ?? form?.title ?? '';
  // CBG-RC9-MULTI: every set the consent names — the form's list once read, the page's until then.
  const sets = formSets(form) ?? pageSets(closedSession, alsoNames, sessionOrder);
  const severalSets = sets.length > 1;
  const closedSets = closedSessionsAmong(sets);

  const create = useMutation({
    mutationFn: async (): Promise<string> => {
      const { data } = await operations.officerCreateBoardMinutesApprovalConsent({
        path: { org_ref: orgRef, record_id: recordId },
        body: { title: shownTitle.trim(), consent_date: consentDate, body },
        throwOnError: true,
      });
      return data.id;
    },
    onSuccess: (consentId) => {
      toast.success('Draft saved. Nothing has gone to the board yet.');
      onCreated(consentId);
    },
    onError: (caught) =>
      // The backend's own sentence: minutes a consent can't approve say which case it is.
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t save this draft. Please try again.',
      ),
  });

  const canSave =
    form !== null && shownTitle.trim().length > 0 && consentDate.length > 0 && !create.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !create.isPending && onClose()}>
      <Dialog.Content
        aria-label={rc9.dialogTitle}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="minutes-consent-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {rc9.dialogTitle}
        </Dialog.Title>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {severalSets ? several.dialogLead(sets.length) : rc9.dialogLead}
        </p>

        {/* A property row: the quiet label beside what the consent will approve. */}
        <div
          className="grid grid-cols-1 gap-4 border-t border-border-subtle pt-12 sm:grid-cols-[132px_minmax(0,1fr)] sm:gap-16"
          data-testid="minutes-consent-approves"
        >
          <span className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary">
            {rc9.approves}
          </span>
          {severalSets ? (
            // Frame m2: one row per set, in the consent's order.
            <span className="flex min-w-0 flex-col gap-6">
              {setsAsRows(
                sets,
                { title: meetingTitle, date: meetingDate },
                { keepDateTogether: true },
              ).map((row, index) => (
                <span
                  key={index}
                  className="font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong"
                  data-testid={`minutes-consent-approves-set-${index}`}
                >
                  {row}
                </span>
              ))}
            </span>
          ) : (
            <span className="min-w-0 font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong">
              {closedSession === null
                ? theMinutesOf(meetingTitle, meetingDate, { keepDateTogether: true })
                : theClosedMinutesOf(meetingTitle, meetingDate, closedSession.purposeLabel, {
                    keepDateTogether: true,
                  })}
            </span>
          )}
        </div>
        {/* CBG-RC11 (frame m2): what approving minutes is — never the consent's own text, which
            stays the board's (C17). */}
        <FieldNote testId="minutes-consent-record-not-decisions">
          {rc11.consentRecordNotDecisions}
        </FieldNote>
        {severalSets ? (
          closedSets > 0 ? (
            // CBG-RC9-MULTI (not drawn): who reads a consent naming a closed session's minutes, and
            // which of its minutes it names by the session's purpose.
            <FieldNote testId="minutes-consent-closed-note">
              {several.closedNote(closedSets, committeeMinutes)}
            </FieldNote>
          ) : null
        ) : closedSession !== null ? (
          // C11 (not drawn): who reads a consent naming a closed session's minutes.
          <FieldNote testId="minutes-consent-closed-note">
            {rc11.consentClosedNote(committeeMinutes)}
          </FieldNote>
        ) : null}

        {formQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-lg" testId="minutes-consent-form-loading" />
        ) : null}

        {formQuery.isError ? (
          <ErrorMessage testId="minutes-consent-form-error">
            {isGivmoError(formQuery.error) ? formQuery.error.message : rc9.formUnavailable}
          </ErrorMessage>
        ) : null}

        {form !== null ? (
          <>
            <TemplateNotice
              orgRef={orgRef}
              reviewLine={form.review_line}
              provenance={form.provenance}
              sourceCitations={form.source_citations}
              testId="minutes-consent-notice"
            />

            <TextField
              label="Title"
              value={shownTitle}
              onChange={(event) => setTitle(event.target.value)}
              testId="minutes-consent-title"
            />
            <TextField
              label="Date this consent bears"
              type="date"
              value={consentDate}
              onChange={(event) => setConsentDate(event.target.value)}
              testId="minutes-consent-date"
            />
            <FieldNote>
              This date is part of what a signature names, so it is set now rather than later.
            </FieldNote>

            {fields.map((field, index) =>
              isLongFormField(field) ? (
                <Textarea
                  key={field}
                  label={templateFieldLabel(field)}
                  rows={3}
                  value={values[field] ?? ''}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field]: event.target.value }))
                  }
                  testId={`minutes-consent-field-${index}`}
                />
              ) : (
                <TextField
                  key={field}
                  label={templateFieldLabel(field)}
                  value={values[field] ?? ''}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field]: event.target.value }))
                  }
                  testId={`minutes-consent-field-${index}`}
                />
              ),
            )}
            <FieldNote testId="minutes-consent-filled-in">
              {severalSets
                ? several.filledIn({
                    meetingMinutesNamed: sets.some((set) => set.closedSession === null),
                    closedSessions: closedSets,
                  })
                : closedSession === null
                  ? rc9.filledInForYou
                  : rc11.consentFilledInClosed}
            </FieldNote>

            <div className="flex flex-col gap-6" data-testid="minutes-consent-preview">
              <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
                What the consent will say
              </span>
              <pre className="whitespace-pre-wrap [overflow-wrap:anywhere] rounded-md border border-border-subtle bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {marked === null ? (
                  body
                ) : (
                  <>
                    {marked.before}
                    {/* The one line the consent can't go out without (contract §2): marked, quietly. */}
                    <span
                      className="rounded-sm bg-brand-50 text-text-strong [box-decoration-break:clone] [-webkit-box-decoration-break:clone]"
                      data-testid="minutes-consent-fingerprint-line"
                    >
                      {marked.line}
                    </span>
                    {marked.after}
                  </>
                )}
              </pre>
              {stillToFill.length > 0 ? (
                <FieldNote testId="minutes-consent-remaining">
                  {rc9.stillInBrackets(stillToFill.length)}
                </FieldNote>
              ) : null}
            </div>
          </>
        ) : null}

        {error !== null ? (
          <ErrorMessage testId="minutes-consent-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={create.isPending}
            onClick={onClose}
            testId="minutes-consent-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={create.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
            testId="minutes-consent-save"
          >
            Save as a draft
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
